/**
 * Labstep
 *
 * @module components/Entity/Action/Update/AllowedActionsLock/Table
 * @desc Table for Allowed Actions Lock update action
 */

import { EntityActionUpdateContainer as EntityActionUpdate } from 'labstep-web/components/Entity/Action/Update';
import { TableSimpleCollapsible as SimpleTableCollapsible } from 'labstep-web/core/Table/Simple/Collapsible';
import type { TableSimpleCollapsibleProps } from 'labstep-web/core/Table/Simple/Collapsible/types';
import { arrayify } from 'labstep-web/state/reducers/helpers/utils';
import type React from 'react';
import { AllowedActionsLockSections } from './constants';
import type { EntityActionUpdateAllowedActionsLockTableProps } from './types';

export const EntityActionUpdateAllowedActionsLockTable: React.FC<
  EntityActionUpdateAllowedActionsLockTableProps
> = ({ entity }) => {
  const sections = Object.keys(AllowedActionsLockSections).map(
    (section, index) => ({
      isInitiallyUncollapsed: index === 0,
      collapsible: true,
      name: section,
      rows: AllowedActionsLockSections[section].map((action) => {
        return {
          name: action.label,
          section,
          actions: action.value,
        };
      }),
    }),
  );
  const columns: TableSimpleCollapsibleProps['columns'] = [
    {
      header: 'Action',
      content: ({ name }) => (
        <span style={{ marginLeft: '50px', fontWeight: 500 }}>
          {name}
        </span>
      ),
      cellProps: { width: 10 },
    },
    {
      header: 'Allowed',
      content: ({ name, actions }) => {
        const allowedActionsLock =
          entity.default_experiment_allowed_actions_lock || [];
        const isAllowed = arrayify(actions).every((action) =>
          allowedActionsLock.includes(action),
        );
        return (
          <div
            style={{
              display: 'flex',
              gap: '10px',
              fontWeight: 500,
            }}
          >
            <EntityActionUpdate
              entityName={entity.entityName}
              id={entity.id}
              renderIfNoEditPermissions
              body={{
                default_experiment_allowed_actions_lock: isAllowed
                  ? allowedActionsLock.filter(
                      (action) => !arrayify(actions).includes(action),
                    )
                  : [...allowedActionsLock, ...arrayify(actions)],
              }}
              actionComponentProps={{
                dataCy: `components/Entity/Action/Update/AllowedActionsLock/Table:${name}`,
                type: 'checkbox',
                elementProps: {
                  toggle: false,
                  checked: isAllowed,
                },
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <SimpleTableCollapsible
      colSpan={1}
      columns={columns}
      sections={sections}
      fixed={false}
    />
  );
};
